import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import heroVideo from "@/assets/hero-video-bg.mp4";
import mixedUse from "@/assets/mixed-use.jpg";
import hotel from "@/assets/hotel.jpg";
import landSubdivision from "@/assets/land-subdivision.jpg";
import seniorsLiving from "@/assets/seniors-living.jpg";
import ProjectCard from "@/components/ProjectCard";
import { projects } from "@/data/projects";
import { useInsights } from "@/hooks/useInsights";
import { ArrowRight } from "lucide-react";
import SEOHead, { LAANDED_ORG_JSONLD, BASE_URL, OG_IMAGE } from "@/components/SEOHead";

const assetClasses = [
{ name: "Mixed-Use", image: mixedUse, filter: "Mixed-Use" },
{ name: "Hotel", image: hotel, filter: "Hotel" },
{ name: "Land Subdivisions", image: landSubdivision, filter: "Land Subdivision" },
{ name: "Seniors Living", image: seniorsLiving, filter: "Seniors Living" }];


const steps = [
{
  number: "01",
  title: "Site Selection",
  description: "We identify sites with genuine long-term potential."
},
{
  number: "02",
  title: "Planning",
  description: "We navigate approvals with patience and precision."
},
{
  number: "03",
  title: "Delivery",
  description: "We manage development with discipline and accountability."
},
{
  number: "04",
  title: "Partnership",
  description: "We work with aligned partners over the long term."
}];


const Index = () => {
  const { insights } = useInsights(3);

  return (
    <main>
      <SEOHead
        title="LAANDED Property Group | Property Development & Strategic Investments NSW"
        description="LAANDED is a Sydney-based property development and investment company specialising in identifying and unlocking value across residential and commercial real estate. We deploy capital strategically across development, investment, and asset management across NSW."
        canonical={`${BASE_URL}/`}
        ogImage={OG_IMAGE}
        jsonLd={LAANDED_ORG_JSONLD}
        breadcrumbs={[{ name: "Home", url: `${BASE_URL}/` }]} />
      
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-end">
        <div className="absolute inset-0">
          <video
            src={heroVideo}
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover" />

          <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/30 to-foreground/10" />
        </div>
        <div className="relative section-padding pb-8 md:pb-12 w-full">
          <h1 className="font-serif text-2xl lg:text-[2.75rem] leading-tight tracking-tight max-w-2xl mb-4 text-destructive-foreground md:text-3xl">We deploy capital strategically across development, investment, and asset management.

          </h1>
          <p className="text-lg md:text-xl text-primary-foreground/70 mb-8 max-w-lg">Disciplined. Communities. Growth.

          </p>
          <div className="flex flex-wrap gap-3">
            <Button variant="hero" size="lg" asChild>
              <Link to="/developments">View Projects</Link>
            </Button>
            <Button variant="hero-outline" size="lg" asChild className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
              <Link to="/contact">Start a Conversation</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Asset Classes */}
      <section className="section-padding section-spacing">
        <h2 className="heading-section mb-12 max-w-md">Asset Classes</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {assetClasses.map((ac) =>
          <Link
            key={ac.filter}
            to={`/developments?asset=${encodeURIComponent(ac.filter)}`}
            className="group block">

              <div className="aspect-square overflow-hidden bg-muted mb-4">
                <img
                src={ac.image}
                alt={`${ac.name} property development NSW — LAANDED Property Group asset class`}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy" />

              </div>
              <h3 className="heading-card">{ac.name}</h3>
            </Link>
          )}
        </div>
      </section>

      {/* Featured Developments */}
      <section className="section-padding section-spacing bg-secondary/50">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="label-caps mb-3">Our Work</p>
            <h2 className="heading-section max-w-md">Featured Projects</h2>
          </div>
          <Link
            to="/developments"
            className="hidden md:inline-flex items-center gap-2 label-caps hover:text-foreground transition-colors">

            View All <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {projects.
          filter((p) => ["akira", "the-crescent"].includes(p.slug)).
          map((p) =>
          <ProjectCard key={p.slug} project={p} />
          )}
        </div>
      </section>

      {/* How We Work */}
      <section className="section-padding section-spacing">
        <p className="label-caps mb-3">Our Process</p>
        <h2 className="heading-section mb-16 max-w-md">How We Work</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {steps.map((step) =>
          <div key={step.number}>
              <span className="font-serif text-3xl text-primary">{step.number}</span>
              <h3 className="font-serif text-xl mt-3 mb-3 text-foreground">{step.title}</h3>
              <p className="body-base text-sm">{step.description}</p>
            </div>
          )}
        </div>
      </section>

      {/* Insights */}
      {insights.length > 0 &&
      <section className="section-padding section-spacing">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="label-caps mb-3">Knowledge</p>
              <h2 className="heading-section max-w-md">Insights</h2>
            </div>
            <Link
            to="/insights"
            className="hidden md:inline-flex items-center gap-2 label-caps hover:text-foreground transition-colors">

              View All <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {insights.map((insight) =>
          <Link
            key={insight.id}
            to={`/insights/${insight.slug}`}
            className="group block border-t border-border pt-6">

                <p className="label-caps mb-3">
                  {insight.published_at ?
              new Date(insight.published_at).toLocaleDateString("en-AU", {
                day: "numeric",
                month: "long",
                year: "numeric"
              }) :
              ""}
                </p>
                <h3 className="heading-card mb-3 group-hover:text-muted-foreground transition-colors">
                  {insight.title}
                </h3>
                {insight.excerpt &&
            <p className="body-base text-sm">{insight.excerpt}</p>
            }
              </Link>
          )}
          </div>
        </section>
      }

      {/* Contact CTA */}
      <section className="section-padding section-spacing border-t border-border">
        <div className="max-w-xl">
          <h2 className="heading-section mb-4">Let's talk.</h2>
          <p className="body-large mb-8">
            We partner with aligned landowners and capital.
          </p>
          <Button variant="hero" size="lg" asChild>
            <Link to="/contact">Get in Touch</Link>
          </Button>
        </div>
      </section>
    </main>);

};

export default Index;